import {
  useRef,
  ReactHTML,
  useEffect,
  useCallback,
  ReactElement,
  createElement,
  HTMLAttributes,
  DetailedHTMLProps,
} from 'react';

import { autoTextSize, Options } from './utils';

/**
 * Make text fit container, prevent overflow and underflow.
 */

type AutoTextSizeProps = Options & {
  as?: keyof ReactHTML;
} & DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export function AutoTextSize({
  mode,
  children,
  flexGrow,
  minFontSizePx,
  maxFontSizePx,
  as: Component,
  fontSizePrecisionPx,
  ...rest
}: AutoTextSizeProps): ReactElement {
  const updateTextSizeRef = useRef<ReturnType<typeof autoTextSize>>();

  useEffect(() => updateTextSizeRef.current?.(), [children]);

  const refCallback = useCallback(
    (innerEl: HTMLElement | null) => {
      updateTextSizeRef.current?.disconnect();

      const containerEl = innerEl?.parentElement;
      if (!innerEl || !containerEl) return;

      updateTextSizeRef.current = autoTextSize({
        innerEl,
        containerEl,
        mode,
        flexGrow,
        minFontSizePx,
        maxFontSizePx,
        fontSizePrecisionPx,
      });
    },
    [mode, flexGrow, minFontSizePx, maxFontSizePx, fontSizePrecisionPx]
  );

  if (typeof Component === 'string') {
    return createElement(Component, { ref: refCallback, ...rest }, children);
  }

  return (
    <div ref={refCallback} {...rest}>
      {children}
    </div>
  );
}
